/**
 * WBS Status tab (formerly "Building Status") — single home for WBS tracking:
 * - building completion cards (§F: progressPercentCompletion UNCHANGED)
 * - per-building stage tables, split by scope:
 *     FLOOR-scoped     → floors-achieved summary + Define Targets (per-floor
 *                        target dates via bulk-plan) + per-floor detail editor
 *     BUILDING/PROJECT → single target date + achieved date + image per stage
 * - real-time floor × stage status matrix (green check = achieved) with
 *   building-wide statuses shown separately (WbsStatusMatrix)
 * Achieved dates recorded here drive the building completion % (recomputed
 * from events on the backend, floor-aware).
 */
import React, { useState, useEffect } from "react";
import axiosInstance from "../../services/api/axios";
import Select from "react-select";
import "react-datepicker/dist/react-datepicker.css";
import usePrivilege from "../../hooks/usePrivilege";
import moment from "moment";
import { SecondaryButton, SettingsButton } from "../Buttons";
import { ClipboardCheck, CalendarClock, LayoutGrid, X } from "lucide-react";
import SummaryCard from "../SummaryCard";
import StatusTypeDetail from "./StatusTypeDetail";
import WbsStatusMatrix from "./WbsStatusMatrix";
import DefineTargetsDialog from "./DefineTargetsDialog";
import BuildingStageModal from "./BuildingStageModal";
import { useUser } from "../../context/UserContext";
import { useWbsStages, isPlinth, isFloorScoped, WBS_TYPE } from "../../constants/wbsStages";
import { getConstructionEvents } from "../../services/wbs/index";

const customStyles = {
  menu: (provided, state) => ({
    ...provided,
    zIndex: 2,
    width: "400px",
    maxHeight: "200px",
    overflowY: "auto",
    ...(state.placement === "top" ? { bottom: "auto", marginTop: "2px" } : { top: "auto", marginBottom: "9px" }),
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    borderRadius: "4px",
    border: "1px solid #ccc",
    backgroundColor: "white",
  }),
  control: (provided, state) => ({
    ...provided,
    zIndex: 2,
    backgroundColor: "white",
    border: "1px solid #ccc",
    borderRadius: "4px",
    boxShadow: state.isFocused ? "0 0 0 1px gray" : null,
    "&:hover": {
      borderColor: state.isFocused ? "gray" : "#aaa",
    },
  }),
  option: (provided, state) => ({
    ...provided,
    padding: "8px 12px",
    fontSize: "14px",
    color: state.isSelected ? "white" : "black",
    backgroundColor: state.isSelected ? "gray" : "white",
    "&:hover": {
      backgroundColor: state.isSelected ? "gray" : "#f0f0f0",
      cursor: "pointer",
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: "14px",
    color: "black",
  }),
  placeholder: (provided) => ({
    ...provided,
    fontSize: "14px",
    color: "#777",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: "#aaa",
    "&:hover": {
      color: "#333",
    },
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: "none",
  }),
};

function BuildingStatus({ projectId, projectWiseBuildingMetadata, refreshBuildingMetadata, refreshProgressImages, phaseId }) {
  const { userInfo } = useUser();
  const [rows, setRows] = useState([]);
  const [buildingOptions, setBuildingOptions] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const { hasPrivilege } = usePrivilege();
  const allOption = { value: "all", label: "All Buildings" };
  const [isLoading, setIsLoading] = useState(true);
  const [selectedStatusType, setSelectedStatusType] = useState(null);
  const [statusTypeSummary, setStatusTypeSummary] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);
  // Define Targets dialog (floor-wise stages) — stage | null
  const [defineTargetsStage, setDefineTargetsStage] = useState(null);
  // Building-wide stage modal — { stage, event } | null
  const [buildingStageModal, setBuildingStageModal] = useState(null);
  // Status matrix modal
  const [isMatrixOpen, setIsMatrixOpen] = useState(false);

  // catalog from /wbs/stages
  const { stages } = useWbsStages();

  useEffect(() => {
    const fetchBuildingOptions = async () => {
      if (!projectId) return;
      setIsLoading(true);
      try {
        const buildingResponse = await axiosInstance.get(`/projects/buildings`, {
          params: { projectId, projectPhaseId: phaseId, customerId: userInfo.loginIdentifierDetails?.customer?.customerId || userInfo.customerId }
        });
        const buildingsArray = Array.isArray(buildingResponse) ? buildingResponse : (buildingResponse?.items || []);
        const flattenedBuildingOptions = buildingsArray.map((building) => {
          const buildingFloors = Array.from({ length: building.numberOfFloors + 1 }, (_, i) => ({
            value: `${building.buildingId}-${i}`,
            label: `${building.buildingName} - Floor ${i}`,
          }));

          return {
            value: building.buildingId,
            label: building.buildingName,
            projectId: building.projectId,
            floors: buildingFloors,
            numberOfFloors: building.numberOfFloors,
            buildingId: building.buildingId,
            buildingName: building.buildingName,
          };
        });
        setBuildingOptions([allOption, ...flattenedBuildingOptions]);

        if (flattenedBuildingOptions.length > 0) {
          setSelectedBuilding(flattenedBuildingOptions[0]);
        } else {
          setSelectedBuilding(allOption);
        }
      } catch (error) {
        console.error("Error fetching building options:", error);
      } finally {
        setTimeout(() => setIsLoading(false), 200);
      }
    };
    fetchBuildingOptions();
  }, [projectId]);

  // Fetch construction events for the selected building
  const fetchSavedRows = async (buildingId) => {
    setIsUpdating(true);
    try {
      const params = projectId ? { projectId: projectId, projectPhaseId: phaseId || userInfo.customerId } : {};
      const response = await getConstructionEvents(params);
      const responseArray = Array.isArray(response) ? response : (response?.items || []);
      const fetchedRows = responseArray
        .filter((row) => row.building.buildingId === buildingId)
        .map((row) => ({
          ...row,
          buildingId: row.building.buildingId,
          buildingName: row.building.buildingName,
          wbsStage: row.wbsStage,
          status: row.wbsStage?.wbsStageId,
          // raw numeric floor (null for BUILDING/PROJECT-scoped) — used by the matrix
          floorNumber: row.floor !== null && row.floor !== undefined ? row.floor : null,
          floor: row.floor !== null && row.floor !== undefined
            ? `${row.building.buildingId}-${row.floor}`
            : "",
          imageUrl: row.media && row.media.length > 0 ? row.media[0].progressImage : null,
          achievedDate: row.achievedDate ? moment(row.achievedDate).format("YYYY-MM-DD") : null,
          actualStartDate: row.actualStartDate ? moment(row.actualStartDate).format("YYYY-MM-DD") : null,
          targetStartDate: row.targetStartDate ? moment(row.targetStartDate).format("YYYY-MM-DD") : null,
          plannedDate: row.plannedDate ? moment(row.plannedDate).format("YYYY-MM-DD") : null,
        }));

      setRows(fetchedRows);
      generateStatusTypeSummary(fetchedRows);
    } catch (error) {
      console.error("Error fetching construction events:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  // Summary iterates catalog stages (excl. CUSTOM); split by scope at render time
  const generateStatusTypeSummary = (fetchedRows) => {
    if (!stages || stages.length === 0) return;

    // Filter catalog: active construction stages excluding CUSTOM
    const filteredStages = stages.filter(
      (s) => s.isActive && s.isConstruction && s.typeName !== WBS_TYPE.CUSTOM
    );

    const summary = filteredStages.map((stage) => {
      const stageRows = fetchedRows.filter(
        (row) => row.wbsStage?.wbsStageId === stage.wbsStageId
      );

      let latestFloor = -1;
      let latestDate = null;

      stageRows.forEach((row) => {
        const floorNumber = row.floorNumber ?? 0;
        if (floorNumber >= latestFloor && row.achievedDate) {
          latestFloor = floorNumber;
          latestDate = row.achievedDate;
        }
      });

      const achievedCount = stageRows.filter((row) => !!row.achievedDate).length;
      const plannedCount = stageRows.filter((row) => !!row.plannedDate).length;
      // Single event for BUILDING/PROJECT-scoped stages (floor IS NULL; legacy floor-0 fallback)
      const buildingEvent = !isFloorScoped(stage)
        ? stageRows.find((row) => row.floorNumber === null) || stageRows.find((row) => row.floorNumber === 0) || null
        : null;

      return {
        // Use the stage object as the statusType key for compatibility with StatusTypeDetail
        statusType: { value: stage.wbsStageId, label: stage.label, typeName: stage.typeName },
        latestFloor,
        latestDate,
        totalFloors: stageRows.length,
        hasData: stageRows.length > 0,
        achievedCount,
        plannedCount,
        buildingEvent,
        stage, // full WbsStageData
      };
    });

    setStatusTypeSummary(summary);
  };

  // Regenerate summary whenever catalog or rows change
  useEffect(() => {
    if (rows.length > 0 && stages.length > 0) {
      generateStatusTypeSummary(rows);
    }
  }, [stages]);

  useEffect(() => {
    if (selectedBuilding && selectedBuilding.value !== "all" && selectedBuilding.buildingId) {
      fetchSavedRows(selectedBuilding.buildingId);
    }
  }, [selectedBuilding, projectId]);

  const handleBuildingChange = (selectedOption) => {
    setSelectedBuilding(selectedOption);
  };

  const handleStatusTypeClick = (statusType) => {
    setSelectedStatusType(statusType);
    if (selectedBuilding?.value === "all" && buildingOptions.length > 1) {
      const firstBuilding = buildingOptions.find(b => b.value !== "all");
      if (firstBuilding) {
        setSelectedBuilding(firstBuilding);
      }
    }
  };

  const handleBackToStatusTypes = () => {
    setSelectedStatusType(null);
    if (selectedBuilding && selectedBuilding.value !== "all" && selectedBuilding.buildingId) {
      fetchSavedRows(selectedBuilding.buildingId);
    }
  };

  const refreshSelectedBuilding = () => {
    if (selectedBuilding && selectedBuilding.value !== "all" && selectedBuilding.buildingId) {
      fetchSavedRows(selectedBuilding.buildingId);
    }
    if (refreshBuildingMetadata) refreshBuildingMetadata();
  };

  // §F: progressPercentCompletion read UNCHANGED
  const getProgressPercentCompletion = (buildingId) => {
    const buildingData = projectWiseBuildingMetadata?.find(b => b.building?.buildingId === buildingId);
    return buildingData?.progressPercentCompletion || 0;
  };

  if (selectedStatusType) {
    return (
      <StatusTypeDetail
        projectId={projectId}
        phaseId={phaseId}
        statusType={selectedStatusType}
        buildingOptions={buildingOptions}
        onBack={handleBackToStatusTypes}
        refreshBuildingMetadata={refreshBuildingMetadata}
        refreshProgressImages={refreshProgressImages}
        selectedBuilding={selectedBuilding}
        onDataUpdate={() => {
          if (selectedBuilding && selectedBuilding.value !== "all" && selectedBuilding.buildingId) {
            fetchSavedRows(selectedBuilding.buildingId);
          }
        }}
      />
    );
  }

  return (
    <div className="">
      {/* Building Completion Cards — §F: progressPercentCompletion UNCHANGED */}
      <div className="">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Building Completion Status</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {buildingOptions.filter(b => b.value !== "all").map((building) => {
            const buildingId = building.buildingId;
            const progressPercentCompletion = getProgressPercentCompletion(buildingId);
            return (
              <SummaryCard
                key={building.value}
                icon={<ClipboardCheck size={24} />}
                title={building.buildingName}
                value={`${progressPercentCompletion.toFixed(2)}%`}
              />
            );
          })}
        </div>
      </div>

      <div className="flex-1 overflow-hidden">
        <div className="py-4 flex flex-col gap-1 h-full">
          {!selectedStatusType && (
            <div className="flex items-end justify-between gap-4 mb-6">
              <div className="flex-1">
                <label className="block text-sm font-normal text-gray-700">Select Building</label>
                <Select
                  value={selectedBuilding}
                  onChange={handleBuildingChange}
                  options={buildingOptions}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                  placeholder="Select Building to Filter"
                  className="text-sm w-1/2 md:w-1/3 mt-1"
                  styles={customStyles}
                />
              </div>
              {selectedBuilding?.value !== "all" && (
                <SecondaryButton
                  className="px-4 py-2 text-sm whitespace-nowrap"
                  onClick={() => setIsMatrixOpen(true)}
                  icon={<LayoutGrid className="mr-2 size-4" />}
                  left
                >
                  Status Matrix
                </SecondaryButton>
              )}
            </div>
          )}

          <div className="flex-1 overflow-hidden">
            {isLoading || isUpdating ? (
              <div className="space-y-3 py-4">
                {[1, 2, 3, 4, 5].map((index) => (
                  <div key={index} className="h-8 bg-gray-200 rounded animate-pulse"></div>
                ))}
              </div>
            ) : selectedBuilding?.value === "all" ? (
              <div className="flex justify-center items-center h-full">
                <div className="text-center">
                  <div className="text-gray-500 text-lg mb-2">Select a building to add status</div>
                  <div className="text-gray-400 text-sm">Choose a specific building from the dropdown above to view and manage its status</div>
                </div>
              </div>
            ) : (
              <div className="space-y-8">
                {/* Floor-wise stages — per-floor target dates + per-floor achievement */}
                <div>
                  <h3 className="text-base font-semibold text-gray-800 mb-2">Floor-wise Stages</h3>
                  <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stage</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Floors Achieved</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Latest Achieved</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {statusTypeSummary.filter((summary) => isFloorScoped(summary.stage)).map((summary) => (
                          <tr key={summary.statusType.value} className="hover:bg-gray-50 transition-colors duration-150">
                            <td className="px-4 py-4 whitespace-nowrap text-sm font-normal text-gray-900">
                              {summary.statusType.label}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {summary.achievedCount > 0 ? (
                                <span className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium bg-green-100 text-green-800">
                                  {summary.achievedCount} / {(selectedBuilding?.numberOfFloors ?? 0) + 1} floors
                                </span>
                              ) : summary.plannedCount > 0 ? (
                                <span className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium bg-amber-100 text-amber-700">
                                  {summary.plannedCount} floor{summary.plannedCount > 1 ? "s" : ""} planned
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium bg-gray-100 text-gray-600">
                                  Not Started
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-normal text-gray-900">
                              {summary.latestDate
                                ? `${moment(summary.latestDate).format("MMM DD, YYYY")} (Floor ${summary.latestFloor})`
                                : "—"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div className="flex items-center gap-2">
                                {hasPrivilege("canEdit") && (
                                  <SecondaryButton
                                    className="px-3 py-1.5 text-xs"
                                    onClick={() => setDefineTargetsStage(summary.stage)}
                                    icon={<CalendarClock className="mr-1.5 size-3.5" />}
                                    left
                                  >
                                    Define Targets
                                  </SecondaryButton>
                                )}
                                <SettingsButton
                                  title="Manage floor statuses"
                                  onClick={() => handleStatusTypeClick(summary.statusType)}
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Building-wide stages — one target date + achieved date + image per stage */}
                <div>
                  <h3 className="text-base font-semibold text-gray-800 mb-2">Building-wide Stages</h3>
                  <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stage</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Target Window</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Achieved Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {statusTypeSummary.filter((summary) => !isFloorScoped(summary.stage)).map((summary) => (
                          <tr key={summary.statusType.value} className="hover:bg-gray-50 transition-colors duration-150">
                            <td className="px-4 py-4 whitespace-nowrap text-sm font-normal text-gray-900">
                              {summary.statusType.label}
                              {isPlinth(summary.stage) && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  Floor 0
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-normal text-gray-900">
                              {summary.buildingEvent?.targetStartDate || summary.buildingEvent?.plannedDate ? (
                                <>
                                  {summary.buildingEvent?.targetStartDate
                                    ? moment(summary.buildingEvent.targetStartDate).format("MMM DD")
                                    : "…"}
                                  {" → "}
                                  {summary.buildingEvent?.plannedDate
                                    ? moment(summary.buildingEvent.plannedDate).format("MMM DD, YYYY")
                                    : "…"}
                                </>
                              ) : "—"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {summary.buildingEvent?.achievedDate ? (
                                <span className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium bg-green-100 text-green-800">
                                  {moment(summary.buildingEvent.achievedDate).format("MMM DD, YYYY")}
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium bg-gray-100 text-gray-600">
                                  Not Achieved
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <SettingsButton
                                title="Update status"
                                onClick={() => setBuildingStageModal({ stage: summary.stage, event: summary.buildingEvent })}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {defineTargetsStage && selectedBuilding?.buildingId && (
        <DefineTargetsDialog
          stage={defineTargetsStage}
          buildingId={selectedBuilding.buildingId}
          buildingName={selectedBuilding.buildingName}
          numberOfFloors={selectedBuilding.numberOfFloors ?? 0}
          events={rows}
          onClose={() => setDefineTargetsStage(null)}
          onSaved={refreshSelectedBuilding}
        />
      )}

      {/* Status Matrix modal — wide grid gets its own scrollable surface */}
      {isMatrixOpen && selectedBuilding?.buildingId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="relative bg-white rounded-lg shadow-xl w-[90vw] max-w-6xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Status Matrix — {selectedBuilding.buildingName}</h3>
                <p className="text-sm text-gray-500">
                  Floor-wise stages per floor (green check = achieved); building-wide statuses shown as chips
                </p>
              </div>
              <button
                onClick={() => setIsMatrixOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <WbsStatusMatrix
                stages={stages}
                events={rows}
                numberOfFloors={selectedBuilding.numberOfFloors ?? 0}
              />
            </div>
          </div>
        </div>
      )}

      {buildingStageModal && selectedBuilding?.buildingId && (
        <BuildingStageModal
          stage={buildingStageModal.stage}
          buildingId={selectedBuilding.buildingId}
          buildingName={selectedBuilding.buildingName}
          event={buildingStageModal.event}
          onClose={() => setBuildingStageModal(null)}
          onSaved={refreshSelectedBuilding}
        />
      )}
    </div>
  );
}

export default BuildingStatus;
